import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux'; 
import { bindActionCreators, Dispatch } from 'redux';
import PageHeader from 'components/common/PageHeader';
import Pager from 'components/common/Pager';
import GearButton from 'components/common/GearButton';
import { Table, Card, Form, Row, Col, Button } from 'react-bootstrap'; 
import '../../assets/styles/styles.css';
import '../../assets/styles/security.css'
import { securityActions } from 'store/security';
import { Auth2User, Auth2Group } from 'models/security/auth2GroupsResponse';
import * as models from '../../models'; 
import { userHasPermission } from '../../utils/helpers';
import { UserSecurityModal, SecurityOption } from './userSecurityModal'; 

interface Props extends PropsFromRedux {  
}

interface State {
    tab: string;
    showUserPermissionModal: boolean;
    userPermissionModelUser: Auth2User | null;
    userSearchRequest: models.Auth2UserSearchRequest;
}

class Security extends React.PureComponent<Props, State> {
    tabs: string[] = ["Users", "Permissions", "Groups"]; 

    public state: State = {
        tab: this.tabs[0],
        showUserPermissionModal: false,
        userPermissionModelUser: null,
        userSearchRequest: models.defaultAuth2UserSearchRequest
    };    

    componentDidMount() {     
        this.props.requestGroups();
        this.fetchUserData();
        this.props.requestPermissions(); 
    }
    
    // Helper for the Main User Grid Checkboxes
    static createUserGroupsCheckState(users: Auth2User[], groups: Auth2Group[]) {
        var userChecks: boolean[][] = [];
        for(var i: number = 0; i < users.length; i++) {
            userChecks[i] = [];
            for (var j: number = 0; j < groups.length; j++) {
                userChecks[i][j] = users[i].groupIds.indexOf(groups[j].groupId) > -1;
            }
        }
        return userChecks;
    }

    // Helper for the Main Permission Grid Checkboxes
    static createPermissionGroupCheckState(permissions: models.Auth2Permission[], groups: Auth2Group[]) {
        var permChecks: boolean[][] = [];
        for (var i: number = 0; i < permissions.length; i++) {
            permChecks[i] = [];
            for (var j: number = 0; j < groups.length; j++) {
                permChecks[i][j] = groups[j].permissionIds.indexOf(permissions[i].permissionId) > -1;
            }
        }
        return permChecks;
    }

    public handleTabs = (name: string) => {
        this.setState({tab: name});
    }

    // Direct checkbox update from the Main User Grid
    public updateUserGroup(userId: number, groupId: number, isInGroup: boolean) {
        this.props.updateUserGroupMembership({ userId: userId, groupId: groupId, isInGroup: isInGroup})
    }

    // Direct permission update from the Permission Grid
    public updateGroupPermission(groupId: number, permissionId: number, include: boolean) {
        this.props.updateGroupPermission({ groupId, permissionId, include });
    }

    public openUserPermissionModal(user: Auth2User) {
        this.setState({
            showUserPermissionModal: true, 
            userPermissionModelUser: user
        });
    }

    public closeUserPermissionModal = () => {
        this.setState({showUserPermissionModal: false});
    }

    // --- Handle Apply from the Shared Modal ---
    // Calculates diffs and calls the immediate API actions
    public handleSecurityApply = (selectedRoles: string[], selectedPermissions: string[]) => {
        const user = this.state.userPermissionModelUser;
        if (!user) return;

        // 1. Diff Roles (Groups)
        // Convert strings (from Modal) back to numbers (for API)
        const newGroupIds = selectedRoles.map(Number);
        const oldGroupIds = user.groupIds;

        // Find Added Groups
        newGroupIds.filter(id => !oldGroupIds.includes(id)).forEach(id => {
            this.props.updateUserGroupMembership({ userId: user.userId, groupId: id, isInGroup: true });
        });

        // Find Removed Groups
        oldGroupIds.filter(id => !newGroupIds.includes(id)).forEach(id => {
            this.props.updateUserGroupMembership({ userId: user.userId, groupId: id, isInGroup: false });
        });

        // 2. Diff Permissions
        // Convert strings (from Modal) back to numbers (for API)
        const newPermIds = selectedPermissions.map(Number);
        const oldPermIds = user.permissionIds;

        // Find Added Permissions
        newPermIds.filter(id => !oldPermIds.includes(id)).forEach(id => {
            this.props.updateUserPermission({ userId: user.userId, permissionId: id, include: true });
        });

        // Find Removed Permissions
        oldPermIds.filter(id => !newPermIds.includes(id)).forEach(id => {
            this.props.updateUserPermission({ userId: user.userId, permissionId: id, include: false });
        });

        this.setState({ showUserPermissionModal: false });
    }

    public renderTabs() {        
        let tabContent;

        if (this.props.isLoading) {
            tabContent = <span>loading...</span>
        } else {
            switch (this.state.tab) {
                case 'Users':
                    tabContent = this.renderUsers();
                    break;
                case 'Groups':
                    tabContent = this.renderGroups();
                    break;
                case 'Permissions':
                    tabContent = this.renderPermissions();
                    break;
                default:
                    tabContent = (<span>Under Construction</span>);
            }
        }

        return (
            <React.Fragment>
                {tabContent}
            </React.Fragment>
        );
    }

    public renderPermissions() {
        var permissions = this.props.permissions.resourceList;
        var permChecks = Security.createPermissionGroupCheckState(permissions, this.props.groups);

        var message = 'Displaying ' + permissions.length + ' permissions.';
        if (this.props.isUpdatingGroups) {
            message += '...updating permissions...'; 
        } else if (this.props.isUpdatingGroupsDone) {
            message += '...permissions saved.'; 
        }
        return (
            <React.Fragment>
                <span>{message}</span>
                <Table striped>
                <thead>    
                    <tr>
                        <th>Permission Name</th>
                        {this.props.groups.map((group, i) => (
                            <th key={i}>{group.groupName}</th>
                        ))}
                    </tr>
                </thead>                
                <tbody>
                    {permissions.map((permission, i) => (
                    <tr key={i}>
                        <td>{permission.permissionName}</td>
                        {this.props.groups.map((group, j) => {
                                return (
                                    <td key={j}>
                                        <input type="checkbox"  
                                            id={`group-perm-checkbox-${i}-${j}`}
                                            name='GroupPermissions'
                                            value='GroupPermissions'
                                            checked={permChecks[i][j]}
                                            onChange={() => this.updateGroupPermission(group.groupId, permission.permissionId, !permChecks[i][j])}
                                        />
                                    </td>
                                )
                                })}
                    </tr>
                ))}
                </tbody>                        
            </Table>
            </React.Fragment>
        );
    }
    
    public renderGroups() {
        return (
            <Table striped>
                <thead>    
                    <tr>
                        <th>Group Name</th>
                    </tr>
                </thead>                
                <tbody>
                    {this.props.groups.map((group, i) => (
                        <tr key={i}>
                            <td>{group.groupName}</td>
                        </tr>
                    ))}
                </tbody>                        
            </Table>
        );
    }

    public applyUserSearchFilter = (e: any) => {
        const { name, value, checked } = e.target;
        let userSearchRequest: models.Auth2UserSearchRequest = { ...this.state.userSearchRequest };
        
        switch (name) {
            case 'firstName': userSearchRequest.firstName = value; break;
            case 'lastName': userSearchRequest.lastName = value; break;
            case 'email': userSearchRequest.email = value; break;
            case 'roles': userSearchRequest.role = value; break;
            case 'isActiveOnly': userSearchRequest.isActiveOnly = checked; break; 
        }
        this.setState({userSearchRequest});
    }

    public searchUsers = () => {
        this.fetchUserData();
    }

    public resetSearchUsers = () => {
        this.setState({userSearchRequest: models.defaultAuth2UserSearchRequest}, () => {
            this.fetchUserData();
        });
    }

    public fetchUserData = () => {
        this.props.requestUsers(this.state.userSearchRequest);
    }

    public pagerCallback = (paging: models.Paging) => { 
        const request: models.Auth2UserSearchRequest = {
            ...this.state.userSearchRequest,
            ...paging,
        } 
        this.props.requestUsers(request); 
    };

    public deactivateUser = (userId: number) => {
        this.props.toggleUserActiveFlag(userId);
    }

    public getUserButtonActions = (user: Auth2User) => {
        return [
            {
                name: 'Edit User Permissions', 
                callBack: () => this.openUserPermissionModal(user)
            },
            {
                name: user.isActive ? 'Deactivate User' : 'Activate User', 
                callBack: () => this.deactivateUser(user.userId)
            } 
        ];       
    }; 

    public renderUsers() {
        const { userSearchRequest } = this.state;
        const { usersResponse, groups, isUpdatingUsers, isUpdatingUsersDone } = this.props;

        var userChecks = Security.createUserGroupsCheckState(usersResponse.resourceList, groups);

        var message = 'Found ' + usersResponse.totalCount + ' users.';
        if (isUpdatingUsers) {
            message += '...updating permissions...'; 
        } else if (isUpdatingUsersDone) {
            message += '...permissions saved.'; 
        }       

        return (
            <React.Fragment>
                {(process.env.REACT_APP_FEATURE_USER_SEARCH_ENABLED == "true") && (
                    <Card className="tab-card">
                        <Card.Body>
                            <Card.Title>User Search</Card.Title>
                            <Form>
                                <Row style={{paddingBottom: ".5em"}}>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control name="firstName" placeholder='First Name' value={userSearchRequest.firstName} onChange={this.applyUserSearchFilter}/>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control name="lastName" placeholder='Last Name' value={userSearchRequest.lastName} onChange={this.applyUserSearchFilter}/>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control name="email" placeholder='Email' value={userSearchRequest.email} onChange={this.applyUserSearchFilter}/>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Roles</Form.Label>
                                            <Form.Select name="roles" onChange={this.applyUserSearchFilter}>
                                                <option key={-1} value="">Select a Role</option>
                                                {this.props.groups.map((group, j) => {
                                                    return (<option key={j} value={group.groupId}>{group.groupName}</option>)
                                                })}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row style={{display: "flex", }}> 
                                    <Form.Group style={{width: "50%"}}>
                                        <Form.Check inline={true} name="isActiveOnly" type="checkbox" label="Active Only" id="chkActiveOnly" checked={userSearchRequest.isActiveOnly} onChange={this.applyUserSearchFilter}/>          
                                    </Form.Group>                      
                                </Row>
                                <Row>
                                    <Col>
                                        <Button onClick={this.searchUsers}>Search</Button>
                                        <Button onClick={this.resetSearchUsers}>Reset Filters</Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                )}
                <Card className="claim-card">
                    <Card.Body>
                        <Card.Title>{message}</Card.Title>
                        <Table striped >
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>User</th>
                                    <th>Is Active</th>
                                    {groups.map((group, i) => (
                                        <th key={i}>{group.groupName}</th>
                                    ))}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersResponse.resourceList.map((user, i) => (
                                    <tr key={i} className="align-middle">
                                        <td>{user.userFullName}</td>
                                        <td>{user.userEmail}</td>
                                        <td>{user.isActive ? "Yes" : "No"}</td>
                                        {this.props.groups.map((group, j) => {
                                            return (
                                                <td key={j}>
                                                    <input type="checkbox"
                                                        id={`user-group-checkbox-${i}-${j}`}
                                                        name='UserGroups'
                                                        value='UserGroups'
                                                        checked={userChecks[i][j]}
                                                        onChange={() => this.updateUserGroup(user.userId, group.groupId, !userChecks[i][j])}
                                                    />
                                                </td>
                                            )
                                        })}
                                        <td className="actions-cell overflow-visible">
                                            <div className="d-flex justify-content-end align-items-center">
                                                <GearButton
                                                    actions={this.getUserButtonActions(user)}
                                                    rowId={user.userId.toString()}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                    <div>
                        <Pager
                            className = {"admin-pager"}
                            retrieveDataCallback = {this.pagerCallback}
                            pageSize = {usersResponse.pageSize || 20}
                            currentPage = {usersResponse.pageNo || 1}
                            totalCount = {usersResponse.totalCount}
                            pagesInRange = {3}
                        />
                    </div>
                </Card>
            </React.Fragment>
        )
    }
    
    // --- ADAPTER FUNCTION ---
    // Maps Auth2 models (Numbers) to UserSecurityModal (Strings)
    public renderUserSecurityModal() {
        const { userPermissionModelUser } = this.state;
        const { groups, permissions } = this.props;

        if (!userPermissionModelUser) return null;

        // 1. Map Auth2Groups (Number IDs) -> SecurityOption (String IDs)
        const roleOptions: SecurityOption[] = groups.map(g => ({
            id: g.groupId.toString(), 
            name: g.groupName,
            grantedPermissionIds: g.permissionIds.map(String) // Convert nested numbers to strings
        }));

        // 2. Map Auth2Permissions (Number IDs) -> SecurityOption (String IDs)
        const permOptions: SecurityOption[] = permissions.resourceList.map(p => ({
            id: p.permissionId.toString(),
            name: p.permissionName
        }));

        // 3. Convert User's current IDs to Strings for the modal
        const initialRoleIds = userPermissionModelUser.groupIds.map(String);
        const initialPermIds = userPermissionModelUser.permissionIds.map(String);

        return (
            <UserSecurityModal 
                show={this.state.showUserPermissionModal} 
                onHide={this.closeUserPermissionModal}
                onApply={this.handleSecurityApply}
                userEmail={userPermissionModelUser.userEmail}
                availableRoles={roleOptions}
                availablePermissions={permOptions}
                initialRoles={initialRoleIds}
                initialPermissions={initialPermIds}
                showRolesTab={false}
            />
        );
    }
    
    public render() {
        const { isPermissionsLoading, isLoading} = this.props;
        const {tab} = this.state; 
        
        return (            
            <>
                {userHasPermission("Security Administrator") ? (
                    <>
                    <PageHeader
                        activeTab={tab}
                        getTabName={this.handleTabs}
                        tabs={this.tabs}
                    />
                    {this.renderTabs()}
                    
                    {/* Render the shared modal via the adapter */}
                    {this.state.showUserPermissionModal && this.state.userPermissionModelUser && 
                        this.renderUserSecurityModal()
                    }
                    </>                    
                ) : <h3>USER NOT AUTHORIZED</h3> }
            </>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    groups: state.security.groups.Auth2GroupResponse.resourceList,
    isLoading: state.security.groups.isLoading,
    permissions: state.security.permissions.Auth2PermissionResponse, 
    isPermissionsLoading: state.security.permissions.isPermissionResponseLoading, 
    usersResponse: state.security.users.Auth2UserResponse,
    isUpdatingUsers: state.security.users.isUpdatingUsers,
    isUpdatingUsersDone: state.security.users.isUpdatingUsersDone,
    isUpdatingGroups: state.security.groups.isUpdatingGroups,
    isUpdatingGroupsDone: state.security.groups.isUpdatingGroupsDone,
    isUpdatingUserPermissions: state.security.users.isUpdatingUserPermissions,
    isUpdatingUserPermissionsDone: state.security.users.isUpdatingUserPermissionsDone,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            requestGroups: securityActions.requestGroups,
            requestPermissions: securityActions.requestPermissions, 
            requestUsers: securityActions.requestUsers,
            toggleUserActiveFlag: securityActions.toggleUserActiveFlag,
            updateUserGroupMembership: securityActions.updateUserGroupMembership,
            updateGroupPermission: securityActions.updateGroupPermission,
            updateUserPermission: securityActions.updateUserPermission,
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(Security);